import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import DeclineModal from '../../components/modals/DeclineModal';
import PreviewModal from '../../components/modals/PreviewModal';
import ReuseRecommendationModal from '../../components/modals/ReuseRecommendationModal';
import BaseModal from '../../components/modals/BaseModal';
import FormSidebar from './FormSidebar/FormSidebar';
import Label from '../../components/controls/Form/components/Label';
import useSubmissionUtils from '../../utils/useSubmissionUtils';
import { createMarkup } from '../../utils/validateForm';
import AddMember from './AddMember/AddMember';
import ObservationHours from './ObservationHours/ObservationHours';
import QuestionBlock from './QuestionBlock/QuestionBlock';
import useAuthUtils from '../../utils/useAuthUtils';
import useMessages from '../../utils/useMessages';
import BusyCover from '../../components/controls/BusyCover/BusyCover';
import Button from '../../components/controls/Button';
import Heading from '../../components/controls/Typography/Heading';
import { TextParagraph, TextReset } from '../../components/controls/Typography/TextBody';
import useRecommendation, { FORM_SUBMISSION_MODE } from '../../utils/useRecommendation';
import { Messages } from '../components/messages/Messages';
import { FormError } from '../success/errorMessage';
import { usePrompt } from '../../utils/confirm/usePrompt';

import './RecommendationForm.less';

const RecommendationForm = () => {
  const { formId: recommendationFormId = null } = useParams();
  const submissionUtils = useSubmissionUtils();
  const recommendation = useRecommendation();
  const navigate = useNavigate();
  const auth = useAuthUtils();
  const messages = useMessages();
  const [showModalPreviewSubmission, setShowModalPreviewSubmission] = useState(false);
  const [showModalReusePrompt, setShowModalReusePrompt] = useState(false);
  const [showModalDecline, setShowModalDecline] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [requestError, setRequestError] = useState('');
  const [isDeleted, set_isDeleted] = useState(false);

  usePrompt({
    title: 'You have unsaved changes!',
    message: 'Are you sure you want to leave?',
    when: recommendation.aliasVars.isChanged,
  });

  const tooltipText =
    'Saves your progress and takes you back to the dashboard if you are not yet ready to submit. Return to this form later by clicking the “Continue” button on the dashboard.';
  const tooltipTextGuestUser = 'Saves your progress if you are not yet ready to submit.';

  /**
   *
   * @param {{ isOpened: boolean }} param0
   */
  const getDeclineModalStateSetter =
    ({ isOpened }) =>
    () =>
      setShowModalDecline(isOpened);

  // TODO: consider move into useEvaluation hook later
  // !Guest users only! Calls the declineRecommendation when request is declined
  const onModalResultOfDeclineRequest = async ({ result, message }) => {
    setShowModalDecline(false);
    if (!result) {
      return false;
    }

    messages.clear();

    setRequestError('');
    const isDecliendResult = await recommendation.evaluationRequest.actions.decline(message);

    const {
      isDeclined = false,
      isDeleted = false,
    } = isDecliendResult;

    set_isDeleted(isDeleted);

    if (isDeleted) {
      return;
    }

    if (isDeclined) {
      messages.notifyPurple(
        `${recommendation.evaluationRequest.getApplicantFullName()}'s Request is Successfully Declined`
      );

      if (auth.guest?.hasEvaluatorAccount) {
        submissionUtils.showSuccessSubmissionWithAccount(
          recommendation.aliasVars.id,
          "You've Successfully Declined the Request",
          'declined'
        );
      } else {
        submissionUtils.showSuccessSubmissionWithNoAccount(
          recommendation.aliasVars.id,
          "You've Successfully Declined the Request",
          auth.account?.email.emailAddress ?? ''
        );
      }
    } else {
      setRequestError(
        'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
      );
      messages.notifyRed(
        'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
      );
    }
  };

  /**
   *
   * @param {React.SyntheticEvent} event
   * @param {import('../../utils/useRecommendation').TFormSubmittionState} mode
   */
  const handleFormSubmit = async (event, mode) => {
    event.preventDefault();
    event.stopPropagation();
    setFormErrorMessage('');

    const applicantName = recommendation.evaluationRequest.getApplicantFullName();

    // validate form data before submission
    if (!mode.startsWith(FORM_SUBMISSION_MODE.SaveDraft)) {
      document.querySelectorAll('.evaluation-form input, .evaluation-form textarea')?.forEach(element => {
        /** @type {HTMLInputElement=} */ (element)?.reportValidity();
      });
      const isValid = /** @type {HTMLFormElement=} */ (document.querySelector('.evaluation-form'))?.reportValidity();
      // prioritize showing error message for ratings and the rest of the inputs
      // if any of the other inputs are invalid, show the error message for them first
      // otherwise, show the error message for ratings in case all ratings are set to Not Observed
      if (isValid && recommendation.aliasVars.ratings.active && recommendation.aliasVars.ratings.allSetToNotObserved) {
        window.scrollTo(0, 0);
        setFormErrorMessage(`
            All your ratings are Not Observed. You must select at least one rating on the evaluation form.
            <br/>If you continue to experience difficulty with this form,
            please ensure you are using a supported web browser.
            <br/>The most up-to-date versions of Chrome, Firefox, and Safari are recommended.
            <br/>You can review our <a href="https://help.liaisonedu.com/Letters_by_Liaison_Help_Center/Troubleshooting%2F%2FFAQs/03_Technical_Requirements" target="_blank" rel="noopener noreferrer">browser information here.</a>
            <br/><br/>`);
        return;
      } else if (!isValid) {
        window.scrollTo(0, 0);
        setFormErrorMessage('Please fill out the required fields.');
        return;
      }
    }

    const opResult = await recommendation.actions.save(mode);
    return { ...opResult, applicantName };
  };

  /**
   * Calls the below API on hitting submit on modal to complete the recommendation?.
   * @param {boolean} confirmSubmission
   */
  const onModalResultOfPreviewSubmission = async confirmSubmission => {
    setShowModalPreviewSubmission(false);
    if (!confirmSubmission) {
      return;
    } else if (recommendation.isEligibleForAutoFill() && auth.isAccountAuth()) {
      // show prompt of re-usable recommendation;
      // this modal invokes the `formOp.complete()` fn at the post-close
      setShowModalReusePrompt(true);
    } else if (recommendation.isEligibleForAutoFill() && auth.isGuestAuth()) {
      // just submit and mark this api with implicit reusable param
      return await recommendation.actions.complete({ reUseState: false });
    } else {
      return await recommendation.actions.complete();
    }
  };

  /**
   * Hadles the ReuseModal answer
   * @param {boolean} reUseState
   */
  const onModalResultOfReusePrompt = async reUseState => {
    setShowModalReusePrompt(false);
    return await recommendation.actions.complete({ reUseState });
  };

  const renderWaiveStatement = () => {
    const statement = [
      recommendation.evaluationRequest.getApplicantFullName(),
      recommendation.evaluationRequest.isWaived()
        ? 'chose to waive their right to receive a copy of your recommendation'
        : 'chose <b>not</b> to waive their right to receive a copy of your recommendation',
    ].join(' ');
    return (
      <div className="waive-statement" id={`${recommendationFormId}-waive-statement`}>
        <i className="far fa-info-circle pr-2" aria-hidden />
        <span dangerouslySetInnerHTML={{ __html: statement }}></span>
      </div>
    );
  };

  const renderFormButtons = () => {
    return (
      <div className="lsn-form-buttons">
        {/* Save to draft buttons */}
        {auth.isAccountAuth() ? (
          <>
            <Button
              primary
              outline
              type="button"
              tooltip={tooltipText}
              aria-label={'Save & Return button. ' + tooltipText}
              onClick={e => {
                setRequestError('');
                handleFormSubmit(e, FORM_SUBMISSION_MODE.SaveDraftAndReturn).then(opResult => {
                  window.scrollTo(0, 0);
                  if (opResult?.isDeleted) {
                    set_isDeleted(true);
                    return;
                  }
                  if (opResult?.isSaved) {
                    navigate('/dashboard');
                    messages.clear().notifyYellow(`${opResult?.applicantName}'s Request is Saved`);
                    return;
                  }
                  if (opResult?.isSaved === false) {
                    setRequestError(
                      'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                    );
                    messages
                      .clear()
                      .notifyRed(
                        'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                      );
                  }
                });
              }}
              disabled={recommendation.busyState.savingToSubmit || isDeleted}
              isBusy={recommendation.busyState.savingToReturn}
            >
              Save & Close
            </Button>
            <Button
              primary
              outline
              type="button"
              tooltip={'Saves your progress if you are not yet ready to submit.'}
              aria-label={'Save button. ' + 'Saves your progress if you are not yet ready to submit.'}
              onClick={e => {
                setRequestError('');
                handleFormSubmit(e, FORM_SUBMISSION_MODE.SaveDraft).then(opResult => {
                  window.scrollTo(0, 0);
                  if (opResult?.isDeleted) {
                    set_isDeleted(true);
                    return;
                  }
                  if (opResult?.isSaved) {
                    messages.clear().notifyYellow(`${opResult?.applicantName}'s Request is Saved`);
                    return;
                  }
                  if (opResult?.isSaved === false) {
                    setRequestError(
                      'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                    );
                    messages
                      .clear()
                      .notifyRed(
                        'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                      );
                  }
                });
              }}
              disabled={recommendation.busyState.savingToSubmit || isDeleted}
              isBusy={recommendation.busyState.savingToDraft}
            >
              Save
            </Button>
          </>
        ) : (
          <Button
            primary
            outline
            type="button"
            tooltip={tooltipTextGuestUser}
            aria-label={'Save button. ' + tooltipTextGuestUser}
            onClick={e => {
              setRequestError('');
              handleFormSubmit(e, FORM_SUBMISSION_MODE.SaveDraft).then(opResult => {
                window.scrollTo(0, 0);
                if (opResult?.isDeleted) {
                  set_isDeleted(true);
                  return;
                }
                if (opResult?.isSaved) {
                  messages.clear().notifyYellow(`${opResult?.applicantName}'s Request is Saved`);
                  return;
                }
                if (opResult?.isSaved === false) {
                  setRequestError(
                    'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                  );
                  messages
                    .clear()
                    .notifyRed(
                      'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                    );
                }
              })
            }}
            disabled={recommendation.busyState.savingToSubmit || isDeleted}
            isBusy={recommendation.busyState.savingToDraft}
          >
            Save
          </Button>
        )}

        {/* Submit Button */}
        {recommendation.evaluationRequest.isObservation() ? (
          <Button
            type="button"
            primary
            onClick={e => {
              setRequestError('');
              handleFormSubmit(e, FORM_SUBMISSION_MODE.ObservationHoursSubmit).then(opResult => {
                window.scrollTo(0, 0);
                if (opResult?.isDeleted) {
                  set_isDeleted(true);
                  return;
                }
                if (opResult?.isSaved) {
                  messages.clear().notifyGreen(`${opResult?.applicantName}'s Request is Submitted`);
                  return;
                }
                if (opResult?.isSaved === false) {
                  setRequestError(
                    'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                  );
                  messages
                    .clear()
                    .notifyRed(
                      'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                    );
                }
              })
            }}
            isBusy={recommendation.busyState.savingToSubmit}
            disabled={recommendation.busyState.savingNonSubmit || isDeleted}
          >
            Submit
          </Button>
        ) : (
          <Button
            type="button"
            primary
            onClick={e => {
              setRequestError('');
              handleFormSubmit(
                e,
                recommendation.evaluationRequest.isCommittee()
                  ? FORM_SUBMISSION_MODE.PreviewCommitteeAndSubmit
                  : FORM_SUBMISSION_MODE.PreviewAndSubmit
              ).then(opResult => {
                window.scrollTo(0, 0);
                if (opResult?.isDeleted) {
                  set_isDeleted(true);
                  return;
                }
                if (opResult?.isSaved) {
                  setShowModalPreviewSubmission(true);
                  return;
                }
                if (opResult?.isSaved === false) {
                  setRequestError(
                    'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                  );
                  messages
                    .clear()
                    .notifyRed(
                      'We were not able to complete your request. Please try again. If this issue persists, please contact support.'
                    );
                }
              })
            }}
            isBusy={recommendation.busyState.savingToSubmit}
            disabled={recommendation.busyState.savingNonSubmit || isDeleted}
          >
            Preview & Submit
          </Button>
        )}
      </div>
    );
  };

  const renderRecommendationForm = () => {
    return recommendation.busyState.loading ? (
      <BusyCover isBusy={recommendation.busyState.busy} />
    ) : recommendation.aliasVars.notFound || isDeleted ? (
      <main className="lsn-page lsn-page-recommendation lsn-container">
        <div className="lsn-request-header no-focus-outline mb-2">
          {auth.isAccountAuth() ? (
            <Button
              link
              icon={<i className="fa fa-chevron-left" aria-hidden="true"></i>}
              primary
              className="mb-2"
              onClick={() => {
                // clears the on-screen messages when nav manually
                messages.clear();
                navigate('/dashboard');
              }}
            >
              Back to Requests
            </Button>
          ) : null}
          <Heading className="text-center no-margin mt-2" tabIndex={0}>
            Request No Longer Exists
          </Heading>
          <FormError
            icon="fas fa-times-circle"
            errorMessage1="This evaluation request no longer exists."
            errorMessage2="Please contact the applicant directly with any questions."
          />
        </div>
      </main>
    ) : (
      <main className="lsn-page lsn-page-recommendation lsn-container">
        <Messages />
        <div className="lsn-request-header no-focus-outline mb-2">
          {auth.isAccountAuth() ? (
            <Button
              link
              icon={<i className="fa fa-chevron-left" aria-hidden="true"></i>}
              primary
              className="mb-2"
              onClick={() => {
                // clears the on-screen messages when nav manually
                messages.clear();
                navigate('/dashboard');
              }}
            >
              Back to Requests
            </Button>
          ) : null}
          {recommendation.evaluationRequest.isObservation() ? (
            <Heading
              className="text-left no-margin mt-2"
              tabIndex={0}
              aria-describedby={`${recommendationFormId}-waive-statement`}
            >
              {'Observation Hours - '}
              {recommendation.evaluationRequest.getApplicantFullName()}
            </Heading>
          ) : (
            <Heading
              className="text-left no-margin mt-2"
              tabIndex={0}
              aria-describedby={`${recommendationFormId}-waive-statement`}
            >
              {recommendation.evaluationRequest.isCommittee() ? 'Committee Recommendation - ' : 'Recommendation - '}
              {recommendation.evaluationRequest.getApplicantFullName()}
            </Heading>
          )}
          <div className="status-and-decline-wrapper">
            {renderWaiveStatement()}
            {auth.isGuestAuth() ? (
              <Button danger link onClick={getDeclineModalStateSetter({ isOpened: true })} aria-haspopup="true">
                Decline this request
              </Button>
            ) : null}
          </div>
        </div>
        <div className="panel columns evaluation-panel">
          <BusyCover isBusy={recommendation.busyState.busy} />
          <FormSidebar
            classNames="column col-3 col-md-12"
            recommendationRequest={recommendation.item?.recommendationRequest}
          />
          <form className="evaluation-form column col-9 col-md-12" name="evaluation-form">
            <TextParagraph
              className="color-red mt-2 mb-2 text-center"
              role={`alert`}
              hidden={!formErrorMessage}
              dangerouslySetInnerHTML={{
                __html: formErrorMessage,
              }}
            ></TextParagraph>

            {recommendation.aliasVars.hasInstructions ? (
              <div className="lsn-section-instructions">
                <Heading level={3} bold asSpan tabIndex={0} aria-describedby={`${recommendationFormId}-instructions`}>
                  <i className="far fa-book mr-2"></i>
                  Instructions
                </Heading>
                <TextReset
                  id={`${recommendationFormId}-instructions`}
                  dangerouslySetInnerHTML={createMarkup(recommendation.item?.evaluationFormSection?.instructions ?? '')}
                ></TextReset>
              </div>
            ) : null}

            {!recommendation ? null : recommendation.evaluationRequest.isObservation() ? (
              <>
                <ObservationHours />
                {renderFormButtons()}
              </>
            ) : (
              <>
                <div className="lsn-recommedation-note">
                  <Heading level={3} bold asSpan tabIndex={0} aria-describedby={`${recommendationFormId}-note`}>
                    <i className="far fa-sticky-note mr-2"></i>
                    Note from {recommendation.aliasVars.firstName}:
                  </Heading>
                  <pre id={`${recommendationFormId}-note`}>{recommendation.aliasVars.message}</pre>
                </div>

                <Label required text={'Indicates required field'} className="general-indication-required-fileds" />

                {recommendation.evaluationRequest.isCommittee() ? <AddMember primaryMember={auth.account} /> : null}

                {recommendation.aliasVars.questionBlocks.map(block => (
                  <QuestionBlock key={block.id} blockId={block.id} />
                ))}

                {renderFormButtons()}
              </>
            )}
          </form>
        </div>
      </main>
    );
  };

  // monitor completed state
  useEffect(() => {
    window.scrollTo(0, 0);
    messages.clear();

    const success = recommendation.aliasVars.completed;

    if (typeof success === 'undefined') {
      return;
    }

    if (!success) {
      setFormErrorMessage('Error processing your request. Please try again.');
      messages.notifyRed(`Error processing your request. Please try again.`);
      return;
    }
    if (auth.isAccountAuth()) {
      messages.notifyGreen(`${recommendation.evaluationRequest.getApplicantFullName()}'s Request is Submitted`);
      navigate('/dashboard');
    } else {
      // WHEN GUEST AUTH
      if (recommendation.isEligibleForAutoFill() || recommendation.evaluationRequest.isReUseForFutureEvalsEnable()) {
        submissionUtils.showSuccessSubmissionAndReuseGuestAccess(
          recommendation.aliasVars.id,
          'Thank you for submitting!',
          recommendation.aliasVars.firstName,
          recommendation.aliasVars.lastName,
          recommendation.aliasVars.instanceName,
          recommendation.aliasVars.evaluationType,
          !!auth.guest?.hasEvaluatorAccount,
          false,
          recommendation.evaluationRequest.linkPDF()
        );
      } else if (auth.guest?.hasEvaluatorAccount) {
        submissionUtils.showSuccessSubmissionWithAccount(
          recommendation.aliasVars.id,
          'Thank you for submitting!',
          'completed',
          recommendation.evaluationRequest.linkPDF()
        );
      } else {
        submissionUtils.showSuccessSubmissionWithNoAccount(
          recommendation.aliasVars.id,
          'Thank you for submitting!',
          auth.account?.email.emailAddress ?? '',
          recommendation.evaluationRequest.linkPDF()
        );
      }
    }
  }, [recommendation.aliasVars.completed]);

  useEffect(() => {
    document.title = 'Letters Portal - Recommendation Form';
    messages.clear();

    recommendation.actions.fetch(recommendationFormId).then(evalReq => {
      // Dispatches an action to have the recommendation response to the store
      if (!evalReq) {
        // If the request is not found, it will show the error message
        auth.isGuestAuth()
          ? submissionUtils.pushError(
              'This evaluation request no longer exists.',
              'Please contact the applicant directly with any questions.',
              'Request No Longer Exists',
              'fas fa-times-circle'
            )
          : void 0;
        // when user is not guest, it will show the error message instead of the form
        return;
      }

      if (evalReq.status === 'completed' || evalReq.status === 'expired' || evalReq.status === 'declined') {
        messages.setMessage(`Requesting evaluation is ${evalReq.status} already.`);
        navigate('/dashboard');
      }
    });

    return () => {
      recommendation.actions.dispose();
    };
  }, []);

  // FormSidebar component consists of the recommendation info or that certain applicant.
  // AddMember component is add seen if its a committee member recommendation?.
  // QuestionBlock component has all the question blocks.
  // ObservationHours component is rendered if type of request is observation hours
  return (
    <>
      {renderRecommendationForm()}
      <PreviewModal
        show={showModalPreviewSubmission}
        title={'Recommendation Preview'}
        subtitle={'Please review ' + recommendation.aliasVars.firstName + "'s recommendation:"}
        body={'Ready to submit?'}
        evaluationId={recommendationFormId}
        buttonContinue={'Submit'}
        buttonCancel={'Back'}
        onClose={result => {
          onModalResultOfPreviewSubmission(result).then(opResult => {
            if (opResult?.isDeleted) {
              set_isDeleted(true);
            }
          });
        }}
      />
      <ReuseRecommendationModal
        show={showModalReusePrompt}
        request={recommendation.item?.recommendationRequest}
        onClose={result => {
          onModalResultOfReusePrompt(result).then(opResult => {
            if (opResult.isDeleted) {
              set_isDeleted(true);
            }
          })
        }}
      />
      <BaseModal
        size="small"
        show={!!requestError}
        title={'Error processing your request'}
        body={requestError}
        modalBodyClass={'text-center'}
        modalPrimaryButton={'OK'}
        onClose={() => {
          setRequestError('');
        }}
      />
      <DeclineModal
        show={showModalDecline}
        title={'Decline ' + recommendation.aliasVars.firstName + "'s Request"}
        declineModalInfo={
          'You may leave an optional note to explain why you are declining ' +
          recommendation.aliasVars.firstName +
          "'s request"
        }
        onClose={onModalResultOfDeclineRequest}
      />
    </>
  );
};

export default RecommendationForm;
